<!--
  The URL Review page's help: what the page is for, how companies get here,
  how to work it, and a glossary of every term on it. Slides in from the
  right; closes on ✕, Escape or a click outside.
-->
<template>
  <div class="fixed inset-0 z-50 flex justify-end">
    <div class="absolute inset-0 bg-black/30" @click="$emit('close')"></div>
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="url-review-help-title"
      class="relative w-full max-w-2xl h-full bg-white shadow-xl overflow-y-auto"
    >
      <div class="sticky top-0 bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between">
        <h2 id="url-review-help-title" class="text-lg font-semibold text-gray-800">About URL Review</h2>
        <button
          ref="closeButton"
          type="button"
          class="text-gray-500 hover:text-gray-800 text-xl leading-none px-2"
          aria-label="Close help"
          @click="$emit('close')"
        >✕</button>
      </div>

      <div class="px-6 py-5 space-y-6 text-sm text-gray-700 leading-relaxed">
        <section>
          <h3 class="font-semibold text-gray-800 mb-1">What this page is for</h3>
          <p>
            Many companies come into Aerocle without a website. URL discovery runs
            look for them automatically and save the ones they're sure of. This page is for
            the rest: companies where a run found a likely site but wasn't sure enough to save
            it, or found nothing it trusted. You confirm the right site, say there isn't one,
            or set the company aside for a closer look.
          </p>
          <p class="mt-2">
            A website saved here is treated as vetted: it's the company's definitive site,
            recorded under your name, the same as marking a URL vetted anywhere else in Aerocle.
          </p>
        </section>

        <section>
          <h3 class="font-semibold text-gray-800 mb-1">How companies end up here</h3>
          <p>Each discovery run takes a batch of companies with no website and, for each one:</p>
          <ol class="list-decimal pl-5 mt-1 space-y-1">
            <li>
              <span class="font-medium">Checks the CAGE registry.</span> If the company has a CAGE
              code, the run looks it up on cage.report. A registered website that loads and doesn't
              name a different company is saved straight away. If it names a different company, the
              row comes here.
            </li>
            <li><span class="font-medium">Searches the web</span> for the company's name and location.</li>
            <li>
              <span class="font-medium">Reads each result's page</span> and compares it with our record:
              name, address, phone, industry, CAGE code and email domains.
            </li>
            <li>
              <span class="font-medium">Asks an AI model to score each site</span> from 0 to 100 on
              how likely it is to be this company's own site, and to pick at most one.
            </li>
          </ol>
          <p class="mt-2">Then, by the model's score for its pick:</p>
          <ul class="list-disc pl-5 mt-1 space-y-1">
            <li><span class="font-medium">85 or higher:</span> saved automatically. These never come here.</li>
            <li><span class="font-medium">50 to 84:</span> comes here as <em>Needs review</em>, with the pick as the run's proposal.</li>
            <li><span class="font-medium">Anything else:</span> comes here as <em>No match found</em>.</li>
          </ul>
          <p class="mt-2">
            A site that would have been saved automatically but whose page contains text aimed at AI
            agents is held here for review too.
          </p>
        </section>

        <section>
          <h3 class="font-semibold text-gray-800 mb-1">How to review a company</h3>
          <ol class="list-decimal pl-5 space-y-1">
            <li>Pick a row in the queue on the left. The run's proposal is already chosen.</li>
            <li>
              Check the company's record (name, address, CAGE code) against the candidate sites.
              Open a site in a new tab, use Preview site, or use the Google and CAGE links.
            </li>
            <li>
              Decide:
              <ul class="list-disc pl-5 mt-1 space-y-1">
                <li><span class="font-medium">Use this website</span> if the chosen candidate is the company's site. To use a different site, choose another candidate, or use Enter another URL for one you found yourself.</li>
                <li><span class="font-medium">No website</span> if the company has no site of its own.</li>
                <li><span class="font-medium">Skip</span> if you can't tell and someone should look more closely.</li>
              </ul>
            </li>
          </ol>
          <p class="mt-2">
            Each decision is saved immediately and the page moves on to the next row. To pass over a
            row without deciding it, just select another row; it stays in the queue.
          </p>
          <p class="mt-2">
            <span class="font-medium">What counts as the company's site:</span> its own site. A parent
            or acquirer's site is fine when the company now lives there, and a page within a larger
            site (such as a division's page) is kept as given. Directories, listings, marketplaces
            and parked or for-sale domains are not.
          </p>
        </section>

        <section>
          <h3 class="font-semibold text-gray-800 mb-1">Changing your mind</h3>
          <p>
            Open the row (set Status to Decided) and click <span class="font-medium">Undo</span>. The company's
            website goes back to what it was and the row returns to the queue. Undo is refused if the
            website has been changed since, so it never overwrites someone else's edit. A skipped row
            goes back with <span class="font-medium">Back to the queue</span>.
          </p>
        </section>

        <section>
          <h3 class="font-semibold text-gray-800 mb-2">Glossary</h3>
          <dl class="space-y-3">
            <div v-for="term in GLOSSARY" :key="term.name">
              <dt class="font-medium text-gray-800">{{ term.name }}</dt>
              <dd class="text-gray-700">{{ term.text }}</dd>
            </div>
          </dl>
        </section>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, defineEmits, onMounted, onBeforeUnmount } from 'vue'

const emit = defineEmits(['close'])
const closeButton = ref(null)

const GLOSSARY = [
  { name: 'Run', text: 'One pass of URL discovery over a batch of companies. The Run filter shows each run\'s date and how many of its rows are still pending.' },
  { name: 'Needs review', text: 'The run found a plausible site and scored it 50–84: likely, but not sure enough to save on its own.' },
  { name: 'No match found', text: 'The run found nothing it trusted. These rows often have candidates worth a look, but no proposal.' },
  { name: 'Pending', text: 'Waiting for a decision. Only pending rows are in the queue.' },
  { name: 'Decided', text: 'Someone chose a website or said there isn\'t one. The company has already been updated.' },
  { name: 'Skipped', text: 'Set aside without a decision, for a closer look. Out of the queue for everyone until it\'s put back; nothing on the company changed.' },
  { name: 'Superseded', text: 'The company got a website some other way before this row was decided, so the row was closed and nothing was overwritten.' },
  { name: 'The run\'s proposal', text: 'The site the run would have saved if it had been sure enough, with the model\'s score and its reason.' },
  { name: 'Run\'s confidence (score)', text: 'The AI model\'s 0–100 score for how likely a site is the company\'s own. Green is 85+ (would be saved automatically), blue 68–84, amber 50–67, grey under 50.' },
  { name: 'Candidate', text: 'A site the run considered. Candidates the model scored 0 are hidden. The number beside each is its place in the list.' },
  { name: 'Run\'s pick', text: 'The candidate the model chose as the best match.' },
  { name: 'CAGE code', text: 'The five-character code the government assigns to registered contractors. The CAGE and DLA links look it up.' },
  { name: 'CAGE record', text: 'What cage.report lists for the code: the registered name and location, street addresses, registered website and email addresses. A registered website or email domain is strong evidence, but people sometimes register a site that isn\'t theirs.' },
  { name: 'CAGE-registered', text: 'A candidate that is the website listed on the company\'s CAGE record.' },
  { name: '✓ and ✗ chips', text: 'What matched (green ✓) or contradicted (red ✗) our record on a candidate\'s page: name, location, address, phone, industry and so on. Hover a chip for the evidence. Things that couldn\'t be checked aren\'t shown.' },
  { name: 'Fetch status', text: 'Shown on a candidate when its page couldn\'t be read normally, for example a timeout or a blocked request. The run had less to go on for that site.' },
  { name: 'Wouldn\'t load, but are indexed', text: 'Sites that failed to load but have pages in Google\'s index. They may be real sites that block automated visits. Leads to check by hand, not evidence of a match.' },
  { name: 'Text aimed at AI', text: 'A candidate page contained wording addressed to AI tools ("ignore previous instructions" and the like). Check those sites yourself rather than trusting the run\'s reasoning.' },
  { name: 'No website', text: 'Records that the company has no site of its own. The company is marked vetted with a placeholder so future runs don\'t search for it again.' },
  { name: 'Decided by', text: 'Who made the decision: a person on this page, Claude (reviewing through Claude Desktop), or a spreadsheet (reviews done in workbooks before this page existed).' },
  { name: 'Preview site', text: 'Shows the chosen site beside the details. Scripts are turned off for safety, so some sites show blank; use Open in new tab for those.' },
  { name: 'Keyboard shortcuts', text: 'Off by default. Turn them on with the button at the top right to move between rows and decide from the keyboard; the keys are listed when they\'re on.' },
]

function onKey(e) {
  if (e.key === 'Escape') emit('close')
}

onMounted(() => {
  window.addEventListener('keydown', onKey)
  closeButton.value?.focus()
})
onBeforeUnmount(() => window.removeEventListener('keydown', onKey))
</script>
